import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import useAuthenticatedAxios from "../../../auth/AuthenticatedAxios";
import ImavenLoadingScreen from '../../../Components/Helpers/loading-screen';
import ToastrService from '../../../services/toaster.service';

// ============================================
// SVG ICONS
// ============================================

const Icons = {
  pipeline: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>,
  plus: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  save: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  copy: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
  milestone: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>,
  step: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  check: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  star: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  drag: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="8" y2="6.01"/><line x1="16" y1="6" x2="16" y2="6.01"/><line x1="8" y1="12" x2="8" y2="12.01"/><line x1="16" y1="12" x2="16" y2="12.01"/><line x1="8" y1="18" x2="8" y2="18.01"/><line x1="16" y1="18" x2="16" y2="18.01"/></svg>,
  expand: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>,
  collapse: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="18 15 12 9 6 15"/></svg>,
};

// ============================================
// STYLED COMPONENTS
// ============================================

const ModuleContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  gap: 0;
`;

const Sidebar = styled.div`
  width: 350px;
  min-width: 350px;
  height: 100%;
  background: rgba(15, 20, 35, 0.95);
  border-right: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
`;

const SidebarTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #a5b4fc;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchInput = styled.input`
  width: 100%;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  color: #fff;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const PipelineList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
`;

const PipelineCard = styled.div`
  background: ${props => props.$selected 
    ? 'rgba(99, 102, 241, 0.2)' 
    : 'rgba(25, 30, 45, 0.6)'};
  border: 1px solid ${props => props.$selected 
    ? 'rgba(99, 102, 241, 0.5)' 
    : 'rgba(99, 102, 241, 0.15)'};
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(99, 102, 241, 0.4);
    background: rgba(99, 102, 241, 0.1);
  }
`;

const PipelineCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const PipelineCardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusBadge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    switch (props.$status) {
      case 'active': return 'rgba(34, 197, 94, 0.2)';
      case 'draft': return 'rgba(234, 179, 8, 0.2)';
      case 'archived': return 'rgba(107, 114, 128, 0.2)';
      default: return 'rgba(99, 102, 241, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'active': return '#22c55e';
      case 'draft': return '#eab308';
      case 'archived': return '#9ca3af';
      default: return '#a5b4fc';
    }
  }};
`;

const PipelineCardMeta = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  gap: 12px;
  align-items: center;
`;

const AddButton = styled.button`
  width: 100%;
  padding: 14px;
  margin: 12px;
  margin-top: 0;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
  border: 1px dashed rgba(99, 102, 241, 0.4);
  border-radius: 10px;
  color: #a5b4fc;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%);
    border-color: rgba(99, 102, 241, 0.6);
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, rgba(10, 14, 26, 0.98) 0%, rgba(15, 20, 35, 0.95) 100%);
  overflow: hidden;
`;

const ContentHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ContentTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;

  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border-color: transparent;
    color: #fff;
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4);
    }
  ` : props.$variant === 'danger' ? `
    background: transparent;
    border-color: rgba(239, 68, 68, 0.5);
    color: #ef4444;
    &:hover {
      background: rgba(239, 68, 68, 0.1);
    }
  ` : `
    background: rgba(25, 30, 45, 0.8);
    border-color: rgba(99, 102, 241, 0.3);
    color: #a5b4fc;
    &:hover {
      background: rgba(99, 102, 241, 0.1);
    }
  `}
`;

const ContentBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #a5b4fc;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FormInput = styled.input`
  width: 100%;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 12px 14px;
  color: #fff;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 12px 14px;
  color: #fff;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 12px 14px;
  color: #fff;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  option {
    background: #1a1f2e;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #6366f1;
`;

const CheckboxLabel = styled.label`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
`;

// Milestones section
const MilestonesSection = styled.div`
  margin-top: 24px;
`;

const MilestoneCard = styled.div`
  background: rgba(25, 30, 45, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
`;

const MilestoneHeader = styled.div`
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(99, 102, 241, 0.05);
  border-bottom: 1px solid rgba(99, 102, 241, 0.1);
  cursor: pointer;

  &:hover {
    background: rgba(99, 102, 241, 0.1);
  }
`;

const MilestoneTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  .order {
    width: 24px;
    height: 24px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
  }

  .title {
    font-size: 14px;
    font-weight: 600;
    color: #fff;
  }

  .count {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
  }
`;

const MilestoneActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #a5b4fc;
  }
`;

const MilestoneBody = styled.div`
  padding: 16px;
  display: ${props => props.$expanded ? 'block' : 'none'};
`;

const StepList = styled.div`
  margin-top: 12px;
`;

const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(17, 20, 30, 0.5);
  border: 1px solid rgba(99, 102, 241, 0.1);
  border-radius: 8px;
  margin-bottom: 8px;

  .step-icon {
    color: #22c55e;
  }

  .step-text {
    flex: 1;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.8);
  }

  .step-actions {
    display: flex;
    gap: 4px;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover .step-actions {
    opacity: 1;
  }
`;

const AddStepButton = styled.button`
  width: 100%;
  padding: 10px;
  background: transparent;
  border: 1px dashed rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(99, 102, 241, 0.5);
    color: #a5b4fc;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
  padding: 60px 40px;
`;

const EmptyStateIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;

  svg {
    width: 36px;
    height: 36px;
    color: #6366f1;
  }
`;

const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 12px 0;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  max-width: 400px;
  line-height: 1.6;
`;

// Inline edit input
const InlineInput = styled.input`
  background: rgba(17, 20, 30, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 6px;
  padding: 8px 12px;
  color: #fff;
  font-size: 13px;
  width: 100%;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.6);
  }
`;

// ============================================
// HELPER FUNCTIONS
// ============================================

const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// ============================================
// COMPONENT
// ============================================

const PipelineModule = () => {
  const axios = useAuthenticatedAxios();
  const [loading, setLoading] = useState(true);
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({});
  const [expandedMilestones, setExpandedMilestones] = useState({});
  const [editingStep, setEditingStep] = useState(null);
  const [editingMilestone, setEditingMilestone] = useState(null);

  const fetchPipelines = async (skipReseed = false) => {
    try {
      setLoading(true);
      const response = await axios.get('/api/pipelines');
      if (response.data?.pipelines) {
        const pipelinesList = response.data.pipelines;

        if (!skipReseed && pipelinesList.length > 0) {
          const hasEmpty = pipelinesList.some(p => {
            const totalSteps = (p.milestones || []).reduce((sum, m) => sum + (m.steps?.length || 0), 0);
            return totalSteps === 0;
          });
          if (hasEmpty) {
            try {
              await axios.post('/api/pipelines/reseed');
              const refreshed = await axios.get('/api/pipelines');
              if (refreshed.data?.pipelines) {
                setPipelines(refreshed.data.pipelines);
                setLoading(false);
                return;
              }
            } catch (reseedErr) {
              console.warn('[PipelineModule] Reseed failed:', reseedErr);
            }
          }
        }

        setPipelines(pipelinesList);
      }
    } catch (error) {
      console.error('[PipelineModule] Error fetching pipelines:', error);
      ToastrService.Error('Failed to load pipelines');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPipelines();
  }, []);

  // Select pipeline
  const handleSelectPipeline = (pipeline) => {
    setSelectedPipeline(pipeline);
    setFormData({
      name: pipeline.name || '',
      description: pipeline.description || '',
      category: pipeline.category || '',
      pathway: pipeline.pathway || '',
      status: pipeline.status || 'draft',
      isDefault: pipeline.isDefault || false,
      milestones: pipeline.milestones || [],
    });
    const expanded = {};
    (pipeline.milestones || []).forEach(m => {
      expanded[m.id] = (m.steps?.length || 0) > 0;
    });
    setExpandedMilestones(expanded);
  };

  // Create new pipeline
  const handleCreateNew = () => {
    const newPipeline = {
      _id: null,
      name: 'New Pipeline',
      description: '',
      category: '',
      pathway: '',
      status: 'draft',
      isDefault: false,
      milestones: [],
    };
    setSelectedPipeline(newPipeline);
    setFormData({
      name: 'New Pipeline',
      description: '',
      category: '',
      pathway: '',
      status: 'draft',
      isDefault: false,
      milestones: [],
    });
    setExpandedMilestones({});
  };

  // Save pipeline
  const handleSave = async () => {
    try {
      if (!formData.name || !formData.category) {
        ToastrService.Error('Name and category are required');
        return;
      }

      let response;
      if (selectedPipeline._id) {
        response = await axios.put(`/api/pipelines/${selectedPipeline._id}`, formData);
      } else {
        response = await axios.post('/api/pipelines', formData);
      }

      if (response.data?.pipeline) {
        ToastrService.Success('Pipeline saved successfully');
        fetchPipelines();
        setSelectedPipeline(response.data.pipeline);
      }
    } catch (error) {
      console.error('Error saving pipeline:', error);
      ToastrService.Error('Failed to save pipeline');
    }
  };

  // Delete pipeline
  const handleDelete = async () => {
    if (!selectedPipeline?._id) return;
    
    if (!window.confirm('Are you sure you want to delete this pipeline?')) return;

    try {
      const response = await axios.delete(`/api/pipelines/${selectedPipeline._id}`);
      if (response.data?.success) {
        ToastrService.Success('Pipeline deleted');
        setSelectedPipeline(null);
        fetchPipelines();
      }
    } catch (error) {
      console.error('Error deleting pipeline:', error);
      ToastrService.Error('Failed to delete pipeline');
    }
  };

  // Duplicate pipeline
  const handleDuplicate = async () => {
    if (!selectedPipeline?._id) return;

    try {
      const response = await axios.post(`/api/pipelines/${selectedPipeline._id}/duplicate`);
      if (response.data?.pipeline) {
        ToastrService.Success('Pipeline duplicated');
        fetchPipelines();
        handleSelectPipeline(response.data.pipeline);
      }
    } catch (error) {
      console.error('Error duplicating pipeline:', error);
      ToastrService.Error('Failed to duplicate pipeline');
    }
  };

  // Add milestone
  const handleAddMilestone = () => {
    const newMilestone = {
      id: generateId(),
      title: 'New Milestone',
      order: (formData.milestones?.length || 0) + 1,
      steps: [],
    };
    const updated = [...(formData.milestones || []), newMilestone];
    setFormData({ ...formData, milestones: updated });
    setExpandedMilestones({ ...expandedMilestones, [newMilestone.id]: true });
    setEditingMilestone(newMilestone.id);
  };

  // Delete milestone
  const handleDeleteMilestone = (milestoneId) => {
    const updated = formData.milestones.filter(m => m.id !== milestoneId);
    // Re-order
    updated.forEach((m, idx) => { m.order = idx + 1; });
    setFormData({ ...formData, milestones: updated });
  };

  // Update milestone title
  const handleUpdateMilestoneTitle = (milestoneId, title) => {
    const updated = formData.milestones.map(m => 
      m.id === milestoneId ? { ...m, title } : m
    );
    setFormData({ ...formData, milestones: updated });
  };

  // Toggle milestone expansion
  const toggleMilestone = (milestoneId) => {
    setExpandedMilestones({
      ...expandedMilestones,
      [milestoneId]: !expandedMilestones[milestoneId]
    });
  };

  // Add step to milestone
  const handleAddStep = (milestoneId) => {
    const newStep = {
      id: generateId(),
      text: 'New step',
    };
    const updated = formData.milestones.map(m => {
      if (m.id === milestoneId) {
        return { ...m, steps: [...(m.steps || []), newStep] };
      }
      return m;
    });
    setFormData({ ...formData, milestones: updated });
    setEditingStep(`${milestoneId}-${newStep.id}`);
  };

  // Delete step
  const handleDeleteStep = (milestoneId, stepId) => {
    const updated = formData.milestones.map(m => {
      if (m.id === milestoneId) {
        return { ...m, steps: m.steps.filter(s => s.id !== stepId) };
      }
      return m;
    });
    setFormData({ ...formData, milestones: updated });
  };

  // Update step text
  const handleUpdateStepText = (milestoneId, stepId, text) => {
    const updated = formData.milestones.map(m => {
      if (m.id === milestoneId) {
        return {
          ...m,
          steps: m.steps.map(s => s.id === stepId ? { ...s, text } : s)
        };
      }
      return m;
    });
    setFormData({ ...formData, milestones: updated });
  };

  // Filter pipelines
  const filteredPipelines = pipelines.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <ImavenLoadingScreen text="Loading Pipelines..." />;
  }

  return (
    <ModuleContainer>
      <Sidebar>
        <SidebarHeader>
          <SidebarTitle>
            {Icons.pipeline} Pipelines
          </SidebarTitle>
          <SearchInput
            type="text"
            placeholder="Search pipelines..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SidebarHeader>

        <PipelineList>
          {filteredPipelines.map(pipeline => (
            <PipelineCard
              key={pipeline._id}
              $selected={selectedPipeline?._id === pipeline._id}
              onClick={() => handleSelectPipeline(pipeline)}
            >
              <PipelineCardHeader>
                <PipelineCardTitle>
                  {pipeline.isDefault && <span style={{ color: '#eab308' }}>{Icons.star}</span>}
                  {pipeline.name}
                </PipelineCardTitle>
                <StatusBadge $status={pipeline.status}>{pipeline.status}</StatusBadge>
              </PipelineCardHeader>
              <PipelineCardMeta>
                <span>{pipeline.category}</span>
                <span>•</span>
                <span>{pipeline.milestones?.length || 0} milestones</span>
              </PipelineCardMeta>
            </PipelineCard>
          ))}

          {filteredPipelines.length === 0 && (
            <EmptyStateText style={{ padding: '20px', textAlign: 'center' }}>
              No pipelines found
            </EmptyStateText>
          )}
        </PipelineList>

        <AddButton onClick={handleCreateNew}>
          {Icons.plus} New Pipeline
        </AddButton>
      </Sidebar>

      <MainContent>
        {selectedPipeline ? (
          <>
            <ContentHeader>
              <ContentTitle>{formData.name || 'Untitled Pipeline'}</ContentTitle>
              <HeaderActions>
                {selectedPipeline._id && (
                  <>
                    <ActionButton onClick={handleDuplicate}>
                      {Icons.copy} Duplicate
                    </ActionButton>
                    <ActionButton $variant="danger" onClick={handleDelete}>
                      {Icons.trash} Delete
                    </ActionButton>
                  </>
                )}
                <ActionButton $variant="primary" onClick={handleSave}>
                  {Icons.save} Save
                </ActionButton>
              </HeaderActions>
            </ContentHeader>

            <ContentBody>
              <FormSection>
                <FormRow>
                  <div>
                    <FormLabel>Pipeline Name *</FormLabel>
                    <FormInput
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Study Visa Pipeline"
                    />
                  </div>
                  <div>
                    <FormLabel>Category *</FormLabel>
                    <FormInput
                      type="text"
                      value={formData.category || ''}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="e.g., Study Visa, Work Permit"
                    />
                  </div>
                </FormRow>
              </FormSection>

              <FormSection>
                <FormLabel>Description</FormLabel>
                <FormTextarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what this pipeline is for..."
                />
              </FormSection>

              <FormSection>
                <FormRow>
                  <div>
                    <FormLabel>Pathway (Optional)</FormLabel>
                    <FormInput
                      type="text"
                      value={formData.pathway || ''}
                      onChange={(e) => setFormData({ ...formData, pathway: e.target.value })}
                      placeholder="e.g., Express Entry FSW"
                    />
                  </div>
                  <div>
                    <FormLabel>Status</FormLabel>
                    <FormSelect
                      value={formData.status || 'draft'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="archived">Archived</option>
                    </FormSelect>
                  </div>
                </FormRow>

                <CheckboxRow>
                  <Checkbox
                    type="checkbox"
                    id="isDefault"
                    checked={formData.isDefault || false}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  />
                  <CheckboxLabel htmlFor="isDefault">
                    Set as default pipeline for this category
                  </CheckboxLabel>
                </CheckboxRow>
              </FormSection>

              <MilestonesSection>
                <FormLabel style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {Icons.milestone} Milestones & Steps
                </FormLabel>

                {formData.milestones?.map((milestone) => (
                  <MilestoneCard key={milestone.id}>
                    <MilestoneHeader onClick={() => toggleMilestone(milestone.id)}>
                      <MilestoneTitle>
                        <span className="order">{milestone.order}</span>
                        {editingMilestone === milestone.id ? (
                          <InlineInput
                            type="text"
                            value={milestone.title}
                            onChange={(e) => handleUpdateMilestoneTitle(milestone.id, e.target.value)}
                            onBlur={() => setEditingMilestone(null)}
                            onKeyDown={(e) => e.key === 'Enter' && setEditingMilestone(null)}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <>
                            <span className="title" onDoubleClick={(e) => { e.stopPropagation(); setEditingMilestone(milestone.id); }}>
                              {milestone.title}
                            </span>
                            <span className="count" style={{ color: (milestone.steps?.filter(s => s.text?.trim()).length || 0) === 0 ? 'rgba(234,179,8,0.7)' : undefined }}>
                              ({milestone.steps?.filter(s => s.text?.trim()).length || 0} steps)
                            </span>
                          </>
                        )}
                      </MilestoneTitle>
                      <MilestoneActions>
                        <IconButton onClick={(e) => { e.stopPropagation(); handleDeleteMilestone(milestone.id); }}>
                          {Icons.trash}
                        </IconButton>
                        <IconButton>
                          {expandedMilestones[milestone.id] ? Icons.collapse : Icons.expand}
                        </IconButton>
                      </MilestoneActions>
                    </MilestoneHeader>

                    <MilestoneBody $expanded={expandedMilestones[milestone.id]}>
                      <StepList>
                        {(milestone.steps?.length || 0) === 0 && (
                          <div style={{ padding: '12px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '13px', fontStyle: 'italic' }}>
                            No steps defined yet
                          </div>
                        )}
                        {milestone.steps?.filter(s => s.text?.trim()).map((step) => (
                          <StepItem key={step.id}>
                            <span className="step-icon">{Icons.check}</span>
                            {editingStep === `${milestone.id}-${step.id}` ? (
                              <InlineInput
                                type="text"
                                value={step.text}
                                onChange={(e) => handleUpdateStepText(milestone.id, step.id, e.target.value)}
                                onBlur={() => setEditingStep(null)}
                                onKeyDown={(e) => e.key === 'Enter' && setEditingStep(null)}
                                autoFocus
                              />
                            ) : (
                              <span
                                className="step-text"
                                onDoubleClick={() => setEditingStep(`${milestone.id}-${step.id}`)}
                              >
                                {step.text}
                              </span>
                            )}
                            <div className="step-actions">
                              <IconButton onClick={() => handleDeleteStep(milestone.id, step.id)}>
                                {Icons.trash}
                              </IconButton>
                            </div>
                          </StepItem>
                        ))}
                      </StepList>

                      <AddStepButton onClick={() => handleAddStep(milestone.id)}>
                        {Icons.plus} Add Step
                      </AddStepButton>
                    </MilestoneBody>
                  </MilestoneCard>
                ))}

                <AddButton onClick={handleAddMilestone} style={{ margin: 0 }}>
                  {Icons.plus} Add Milestone
                </AddButton>
              </MilestonesSection>
            </ContentBody>
          </>
        ) : (
          <EmptyState>
            <EmptyStateIcon>{Icons.pipeline}</EmptyStateIcon>
            <EmptyStateTitle>Pipelines</EmptyStateTitle>
            <EmptyStateText>
              Create pipelines to define the stages and steps each case type goes through.
              <br /><br />
              For example, a "Study Visa Pipeline" might have milestones like:
              Initial Contact → Gather Documents → Submit Application → Wait for Decision
            </EmptyStateText>
            <AddButton onClick={handleCreateNew} style={{ marginTop: '24px', width: 'auto', padding: '12px 24px' }}>
              {Icons.plus} Create Your First Pipeline
            </AddButton>
          </EmptyState>
        )}
      </MainContent>
    </ModuleContainer>
  );
};

export default PipelineModule;
